import { LitElement, html } from "lit"
import { customElement, property } from "lit/decorators.js"
import { ifDefined } from "lit/directives/if-defined.js"
import { ref } from "lit/directives/ref.js"
import * as checkedIcon from "@nordhealth/icons/lib/assets/interface-checked-small.js"
import * as closeIcon from "@nordhealth/icons/lib/assets/interface-close-small.js"
import Icon from "../icon/Icon.js"
import { InputMixin } from "../common/mixins/InputMixin.js"
import { FocusableMixin } from "../common/mixins/FocusableMixin.js"
import { NordEvent } from "../common/events.js"
import { FormDataController } from "../common/controllers/FormDataController.js"
import { LocalizeController } from "../localization/LocalizeController.js"

import componentStyle from "../common/styles/Component.css"
import style from "./Tag.css"

Icon.registerIcon(checkedIcon)
Icon.registerIcon(closeIcon)

/**
 * Tags represent a set of keywords that help label, categorize,
 * and organize objects. Commonly used to signify the attributes of an object.
 *
 * @status new
 * @category text
 * @slot - The tag content.
 *
 * @localization removeLabel - Accessible label for the remove button.
 */
@customElement("nord-tag")
export default class Tag extends InputMixin(FocusableMixin(LitElement)) {
  static styles = [componentStyle, style]

  private localize = new LocalizeController<"nord-tag">(this)

  protected formData = new FormDataController(this, { value: () => this.formValue })

  protected get formValue() {
    return this.variant === "selectable" && this.checked ? this.value || "on" : undefined
  }

  protected inputId = "input"

  /**
   * The behavioral variant of the tag.
   */
  @property({ reflect: true }) variant: "default" | "removable" | "selectable" = "default"

  /**
   * Controls whether the tag is checked or not (only relevant when variant is set to `selectable`).
   */
  @property({ reflect: true, type: Boolean }) checked: boolean = false

  /**
   * The size of the tag. This affects the font-size.
   */
  @property({ reflect: true }) size: "s" | "m" = "m"

  /**
   * Controls whether the tag expands to fill the width of its container.
   */
  @property({ reflect: true, type: Boolean }) expand = false

  protected handleChange(e: Event) {
    const target = e.target as HTMLInputElement
    this.checked = target.checked

    e.stopPropagation()

    /**
     * Fired whenever the tag has been checked or unchecked via user interaction.
     */
    this.dispatchEvent(new NordEvent("change"))
  }

  protected handleRemove() {
    /**
     * Fired when the remove button is activated. This event should be used to remove the tag from the DOM.
     */
    this.dispatchEvent(new NordEvent("remove"))
  }

  render() {
    switch (this.variant) {
      case "selectable":
        return this.renderSelectableTag()
      case "removable":
        return this.renderRemovableTag()
      case "default":
      default:
        return this.renderStaticTag()
    }
  }

  // eslint-disable-next-line class-methods-use-this
  private renderStaticTag() {
    return html`
      <div class="n-tag">
        <div class="n-content">
          <slot></slot>
        </div>
      </div>
    `
  }

  private renderRemovableTag() {
    return html`
      <div class="n-tag">
        <div class="n-content">
          <slot></slot>
        </div>
        <button class="n-remove-button" @click=${this.handleRemove} ?disabled=${this.disabled}>
          <nord-icon name="interface-close-small" label=${this.localize.term("removeLabel")}></nord-icon>
        </button>
      </div>
    `
  }

  private renderSelectableTag() {
    return html`
      <label for=${this.inputId} class="n-tag">
        <div class="n-input-container">
          <input
            ${ref(this.focusableRef)}
            class="n-input"
            id=${this.inputId}
            type="checkbox"
            name=${ifDefined(this.name)}
            .value=${this.value}
            .checked=${this.checked}
            ?disabled=${this.disabled}
            @change=${this.handleChange}
          />
          <nord-icon class="icon-checked" name="interface-checked-small"></nord-icon>
        </div>
        <div class="n-content">
          <slot></slot>
        </div>
      </label>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "nord-tag": Tag
  }
}
